// ============================================================================
// ResultatsFinanciersPage (E2, F-503) — SOMMAIRE, /resultats-financiers
// ============================================================================
// Portage du PoC app/(dashboard)/finance/sommaire/page.tsx au standard maison.
//
// C'est la page de la NAV : elle est OUVERTE a tout role GEST interne (employee
// inclus, M-500) — jamais un 403 derriere un item de sidebar. Le DETAIL complet
// (admin, F-500) vit sous /resultats-financiers/detail, atteint par le lien F-504
// affiche SI le serveur dit que le viewer y a droit (`viewer.canViewDetail` — jamais
// un recalcul client, lecon GA-05).
//
// L'inversion des URL vs PoC (/finance = detail, /finance/sommaire = sommaire) est
// VOULUE : la nav pointe la page ouverte. `/resultats` (l'ancien placeholder
// « a venir ») redirige vers cette page — cf. App.tsx.
//
// Deep-link ?period=YYYY-MM (F-507) ; la periode AFFICHEE est celle RETENUE par le
// serveur (M-506), pas le param brut : sans param, l'ecran ouvre sur le dernier mois
// reel saisi.

import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader, SkeletonCard, QueryError, EmptyState, GS_COMPONENTS } from '@gs/ui';
import { ArrowRight, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useFinanceSummary, useFinanceYear } from '../../hooks/useFinance';
import { computeStatement } from '../../lib/finance/computations';
import { FinanceKpiCards } from '../../components/finance/FinanceKpiCards';
import { FinanceYearChart } from '../../components/finance/FinanceYearChart';
import { PeriodPicker } from '../../components/finance/PeriodPicker';
import { formatMonthLong } from './format';

/** Ecran de refus (Q1 corporate-sans-GEST) — l'autorite est le 403 serveur. */
export function FinanceForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:finance.forbidden.title')}
      description={t('gest:finance.forbidden.description')}
    />
  );
}

export default function ResultatsFinanciersPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locale = i18n.language;

  const periodParam = searchParams.get('period'); // F-507 deep-link
  const summaryQ = useFinanceSummary(periodParam, !!user);
  const yearQ = useFinanceYear(periodParam, !!user);

  const forbidden = summaryQ.isError && summaryQ.error instanceof ApiForbiddenError;
  const period = summaryQ.data?.period ?? null;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:finance.summary.title')}
        description={t('gest:finance.summary.description')}
        onRefresh={() => { void summaryQ.refetch(); void yearQ.refetch(); }}
        refreshLoading={summaryQ.isFetching || yearQ.isFetching}
        actions={period ? <PeriodPicker value={period} /> : undefined}
      />

      {forbidden ? (
        <FinanceForbidden />
      ) : summaryQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : summaryQ.isError || !summaryQ.data ? (
        <QueryError message={t('gest:finance.loadError')} onRetry={() => void summaryQ.refetch()} />
      ) : (
        <>
          <p className="text-field text-slate-500 dark:text-slate-400">
            {t('gest:finance.summary.periodLabel', { month: formatMonthLong(summaryQ.data.period, locale) })}
          </p>

          <FinanceKpiCards
            statement={computeStatement(summaryQ.data.lines)}
            summary={summaryQ.data.summary}
            locale={locale}
            t={t}
          />

          {/* F-504 : le lien vers le detail n'apparait que si le SERVEUR l'accorde. */}
          {summaryQ.data.viewer.canViewDetail ? (
            <button
              type="button"
              onClick={() => navigate(`/resultats-financiers/detail?period=${summaryQ.data.period}`)}
              className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
            >
              {t('gest:finance.summary.detailLink')}
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          ) : null}

          {/* Le graphique porte son propre titre : pas de section qui doublonne. */}
          {yearQ.isLoading ? (
            <SkeletonCard lines={6} />
          ) : yearQ.isError || !yearQ.data ? (
            <QueryError message={t('gest:finance.yearChart.error')} onRetry={() => void yearQ.refetch()} />
          ) : (
            <FinanceYearChart points={yearQ.data.points} locale={locale} t={t} />
          )}
        </>
      )}
    </div>
  );
}
